import { useEffect, useRef, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { toast } from 'sonner';

import UpdateToast from '../UpdateToast/UpdateToast';

const SW_UPDATE_INTERVAL_MS = 1000 * 60 * 60; // 1 hour

export default function PWAUpdateToast() {
  const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);
  const toastIdRef = useRef<string | number | null>(null);
  const applyingRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const initialSwSet = new WeakSet<ServiceWorker>();
    let isInitialCycle = true;
    const mountTime = Date.now();

    const handleWaitingSW = (sw: ServiceWorker) => {
      // Skip if: no prior controller (first install), or already applying update
      if (!navigator.serviceWorker.controller || applyingRef.current) return;

      // If this SW was found during the initial load cycle or within first 10 seconds
      // silently skip waiting because index.html is NetworkFirst and already on the latest version.
      const isInitialLoad = isInitialCycle || Date.now() - mountTime < 10000;

      if (isInitialLoad || initialSwSet.has(sw)) {
        sw.postMessage({ type: 'SKIP_WAITING' });
        return;
      }

      setWaitingSW((prev) => (prev === sw ? prev : sw));
    };

    let updateFoundListenerAttached = false;

    const check = async () => {
      if (applyingRef.current) return;

      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;

        if (!updateFoundListenerAttached) {
          updateFoundListenerAttached = true;
          reg.addEventListener('updatefound', () => {
            const installing = reg.installing;
            if (!installing) return;

            if (isInitialCycle) {
              initialSwSet.add(installing);
            }

            installing.addEventListener('statechange', () => {
              if (installing.state === 'installed') handleWaitingSW(installing);
            });
          });
        }

        if (reg.waiting) {
          if (isInitialCycle) initialSwSet.add(reg.waiting);
          handleWaitingSW(reg.waiting);
        }

        await reg.update();
      } catch (err) {
        // Ignore offline or update check errors
      } finally {
        isInitialCycle = false;
      }
    };

    check();

    const interval = setInterval(check, SW_UPDATE_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!waitingSW || toastIdRef.current) return;

    toastIdRef.current = toast.custom(
      (toastId) => (
        <UpdateToast
          icon={<FiRefreshCw />}
          title="Update ready"
          description="Please refresh to load the latest version."
          actionLabel="Refresh"
          onDismiss={() => toast.dismiss(toastId)}
          onAction={() => {
            applyingRef.current = true;
            toast.dismiss(toastId);
            toastIdRef.current = null;

            // Reload only after new SW actually takes control
            navigator.serviceWorker.addEventListener(
              'controllerchange',
              () => window.location.reload(),
              { once: true },
            );

            waitingSW.postMessage({ type: 'SKIP_WAITING' });
          }}
        />
      ),
      {
        duration: Infinity,
        onDismiss: () => {
          toastIdRef.current = null;
        },
      },
    );

    return () => {
      if (toastIdRef.current !== null) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, [waitingSW]);

  return null;
}
